import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
interface ComparableProperty {
  address: string;
  livingArea: string;
  yearBuilt: string;
  lotSize: string;
  lastSold: string;
  soldPrice: string;
  adjustments: {
    condition?: string;
    lotViewsLocation?: string;
    garage?: string;
    livingArea?: string;
    dateOfSale?: string;
    otherFeatures?: string;
  };
  totalAdjustments: string;
  finalAdjustedValue: string;
  additionalNotes?: string;
}
interface AIComparableProperty extends ComparableProperty {
  aiNotes?: string;
  aiAdjustments?: {
    [key: string]: string;
  };
}
const PropertyTaxReport = () => {
  // Sample data - in a real app, this would come from props or API
  const propertyInfo = {
    ownerName: 'patrick+agent null',
    address: '1009 Topaz St, Boulder County, CO, 80027-6041',
    phoneNumber: 'N/A',
    email: 'patrick+agent@yourwayhome.com',
    apn: '1575-31-2-12-064',
    legalDescription: 'LOT 7 BLK 1 ROCK CREEK RANCH FLG 19',
    fairMarketValue: '$819,400.00',
    lotSize: '6,500 sq ft',
    livingAreaSqft: '2,450 sq ft',
    basementSqft: '1,200 sq ft',
    finishedBasementSqft: '800 sq ft',
    beds: '4',
    baths: '3.5',
    garageSizeSqft: '500 sq ft',
    yearBuilt: '1998'
  };
  const assessorInfo = {
    name: 'Boulder County Assessor\'s Office',
    address: '1325 Pearl Street, Boulder, CO 80302',
    phone: '303-441-3530'
  };
  const comparableSales: ComparableProperty[] = [{
    address: '3021 Gardenia Way, Superior, CO 80027',
    livingArea: '2375 sq ft',
    yearBuilt: '1998',
    lotSize: '5401 sq ft',
    lastSold: 'November 13, 2023',
    soldPrice: '$870,000.00',
    adjustments: {
      livingArea: '–$110,600.00'
    },
    totalAdjustments: '–$110,600.00',
    finalAdjustedValue: '$759,400.00'
  }, {
    address: '2320 Clayton Cir, Superior, CO 80027',
    livingArea: '2344 sq ft',
    yearBuilt: '1996',
    lotSize: '12153 sq ft',
    lastSold: 'September 12, 2023',
    soldPrice: '$930,000.00',
    adjustments: {
      livingArea: '+$77,200.00'
    },
    totalAdjustments: '+$77,200.00',
    finalAdjustedValue: '$1,007,200.00'
  }, {
    address: '901 Shavano Peak Dr, Superior, CO 80027',
    livingArea: '2404 sq ft',
    yearBuilt: '2000',
    lotSize: '5576 sq ft',
    lastSold: 'April 23, 2024',
    soldPrice: '$930,000.00',
    adjustments: {
      livingArea: '–$123,600.00'
    },
    totalAdjustments: '–$123,600.00',
    finalAdjustedValue: '$806,400.00'
  }, {
    address: '2570 Clayton Cir, Superior, CO 80027',
    livingArea: '2615 sq ft',
    yearBuilt: '1996',
    lotSize: '7100 sq ft',
    lastSold: 'February 27, 2023',
    soldPrice: '$966,000.00',
    adjustments: {
      livingArea: '–$261,400.00'
    },
    totalAdjustments: '–$261,400.00',
    finalAdjustedValue: '$704,600.00'
  }];
  const aiComparableSales: AIComparableProperty[] = [{
    address: '2925 Jade Ct, Superior, CO 80027',
    livingArea: '2771 sq ft',
    yearBuilt: '2000',
    lotSize: '10498 sq ft',
    lastSold: 'January 3, 2024',
    soldPrice: '$1,198,000.00',
    adjustments: {
      livingArea: '–$274,000.00'
    },
    totalAdjustments: '–$274,000.00',
    finalAdjustedValue: '$924,000.00',
    aiNotes: 'The Comparable Property is superior to the Subject Property in nearly every feature category, including kitchen and bath remodels, flooring, and outdoor living spaces. The finished basement with a home theater and wet bar is a significant value add not present in the Subject. The deck and landscaping are also more extensive and upgraded.',
    aiAdjustments: {
      Kitchen: '-$35,000',
      Bath: '-$25,000',
      Flooring: '-$10,000',
      Windows: 'No adjustment',
      'Exterior Landscape': '-$10,000',
      'Deck/Outdoor Living': '-$20,000',
      'Basement/Home Theater/Wet Bar': '-$40,000',
      'Overall Features': '-$15,000'
    }
  }];
  return <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1 container mx-auto px-4 py-8 md:py-12 max-w-7xl">
        {/* Header Section */}
        <div className="text-center mb-8 md:mb-12">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-3">
            Property Tax Value Report
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Comprehensive valuation analysis and comparable sales review
          </p>
        </div>

        {/* Property Information Card */}
        <Card className="mb-8 shadow-lg">
          <CardHeader className="bg-secondary/30">
            <CardTitle className="text-2xl text-primary">
              Subject Property Information
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-6">
              {/* Owner & Contact Information */}
              <div>
                <h3 className="text-sm font-semibold text-primary mb-3">Owner & Contact Information</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Owner Name</p>
                    <p className="text-base font-semibold text-foreground">{propertyInfo.ownerName}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Email</p>
                    <p className="text-base text-foreground">{propertyInfo.email}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Phone</p>
                    <p className="text-base text-foreground">{propertyInfo.phoneNumber}</p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Property Address & Legal */}
              <div>
                <h3 className="text-sm font-semibold text-primary mb-3">Property Address & Legal</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Property Address</p>
                    <p className="text-base font-semibold text-foreground">{propertyInfo.address}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">APN (Assessor Parcel Number)</p>
                    <p className="text-base font-semibold text-foreground">{propertyInfo.apn}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Legal Description</p>
                    <p className="text-base text-foreground">{propertyInfo.legalDescription}</p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Property Details */}
              <div>
                <h3 className="text-sm font-semibold text-primary mb-3">Property Details</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Lot Size</p>
                    <p className="text-base font-semibold text-foreground">{propertyInfo.lotSize}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Living Area Sqft (above grade)</p>
                    <p className="text-base font-semibold text-foreground">{propertyInfo.livingAreaSqft}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Basement Sqft (total)</p>
                    <p className="text-base font-semibold text-foreground">{propertyInfo.basementSqft}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Finished Basement Sqft</p>
                    <p className="text-base font-semibold text-foreground">{propertyInfo.finishedBasementSqft}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Beds</p>
                    <p className="text-base font-semibold text-foreground">{propertyInfo.beds}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Baths</p>
                    <p className="text-base font-semibold text-foreground">{propertyInfo.baths}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Garage Size Sqft</p>
                    <p className="text-base font-semibold text-foreground">{propertyInfo.garageSizeSqft}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Year Built</p>
                    <p className="text-base font-semibold text-foreground">{propertyInfo.yearBuilt}</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subject Property Photos */}
        <Card className="mb-8 shadow-lg">
          <CardHeader className="bg-secondary/30">
            <CardTitle className="text-2xl text-primary">
              Subject Property Photos
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { src: '/property-photos/front_yard.jpg', label: 'Exterior Front' },
                { src: '/property-photos/backyard.jpg', label: 'Backyard' },
                { src: '/property-photos/kitchen.jpg', label: 'Kitchen' },
                { src: '/property-photos/living_area.jpg', label: 'Living Area' },
                { src: '/property-photos/primary_bathroom.jpg', label: 'Primary Bathroom' }
              ].map((photo, index) => (
                <div key={index} className="space-y-2">
                  <div className="aspect-video overflow-hidden rounded-lg border border-border shadow-sm">
                    <img 
                      src={photo.src} 
                      alt={photo.label}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <p className="text-sm font-medium text-center text-muted-foreground">
                    {photo.label}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Fair Market Value Summary */}
        <Card className="mb-8 shadow-lg border-2 border-primary/20">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm font-medium text-muted-foreground mb-2">
                Estimated Fair Market Value
              </p>
              <p className="text-4xl md:text-5xl font-bold text-primary">
                {propertyInfo.fairMarketValue}
              </p>
              <p className="text-sm text-muted-foreground mt-4 max-w-2xl mx-auto">
                Based on comprehensive analysis of comparable sales with
                appropriate adjustments for size, features, location, condition,
                and date of sales
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Comparable Sales Analysis */}
        <div className="mb-12">
          <div className="mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-2">
              Comparable Sales Analysis
            </h2>
            <p className="text-muted-foreground">
              Detailed analysis of recent comparable sales in the area with
              market-based adjustments
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {comparableSales.map((comp, index) => <Card key={index} className="shadow-md hover:shadow-lg transition-shadow">
                <CardHeader className="bg-secondary/20">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg text-foreground">
                      {comp.address}
                    </CardTitle>
                    <Badge variant="outline" className="ml-2">
                      #{index + 1}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-3 mb-4">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Living Area:</span>
                        <span className="ml-2 font-medium">{comp.livingArea}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Year Built:</span>
                        <span className="ml-2 font-medium">{comp.yearBuilt}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Lot Size:</span>
                        <span className="ml-2 font-medium">{comp.lotSize}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Last Sold:</span>
                        <span className="ml-2 font-medium">{comp.lastSold}</span>
                      </div>
                    </div>
                    <Separator />
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">
                        Sold Price
                      </p>
                      <p className="text-2xl font-bold text-foreground">
                        {comp.soldPrice}
                      </p>
                    </div>
                  </div>

                  <div className="bg-secondary/30 rounded-lg p-4 space-y-2">
                    <p className="text-sm font-semibold text-foreground mb-2">
                      Adjustments:
                    </p>
                    {Object.entries(comp.adjustments).map(([key, value]) => value && value !== 'N/A' && <div key={key} className="flex justify-between text-sm">
                          <span className="text-muted-foreground capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}:
                          </span>
                          <span className={`font-semibold ${value.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                            {value}
                          </span>
                        </div>)}
                    <Separator className="my-2" />
                    <div className="flex justify-between text-sm font-semibold">
                      <span className="text-foreground">Total Adjustments:</span>
                      <span className={comp.totalAdjustments.startsWith('+') ? 'text-green-600' : 'text-red-600'}>
                        {comp.totalAdjustments}
                      </span>
                    </div>
                    <div className="flex justify-between text-base font-bold pt-2">
                      <span className="text-foreground">
                        Final Adjusted Value:
                      </span>
                      <span className="text-primary">
                        {comp.finalAdjustedValue}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>)}
          </div>

          <Card className="mt-6 bg-secondary/20">
            <CardContent className="pt-6">
              <p className="text-center text-foreground">
                The analysis indicates adjusted values between{' '}
                <span className="font-semibold">$704,600.00</span> and{' '}
                <span className="font-semibold">$1,007,200.00</span> with an
                average of{' '}
                <span className="font-bold text-primary text-lg">
                  $819,400.00
                </span>
              </p>
            </CardContent>
          </Card>
        </div>

        {/* AI Comparable Sales Analysis */}
        {aiComparableSales.length > 0 && <div className="mb-12">
            <div className="mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-primary mb-2">
                AI-Enhanced Comparable Sales Analysis
              </h2>
              <p className="text-muted-foreground">
                Advanced analysis with AI photo analysis comparing subject to comparable sales with market based adjustments.                                       
              </p>
            </div>

            <div className="space-y-6">
              {aiComparableSales.map((comp, index) => <Card key={index} className="shadow-lg">
                  <CardHeader className="bg-accent/10">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-xl text-foreground">
                        {comp.address}
                      </CardTitle>
                      <Badge className="ml-2">AI Analysis</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="grid md:grid-cols-3 gap-6 mb-6">
                      <div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              Living Area:
                            </span>
                            <span className="font-medium">{comp.livingArea}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              Year Built:
                            </span>
                            <span className="font-medium">{comp.yearBuilt}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              Lot Size:
                            </span>
                            <span className="font-medium">{comp.lotSize}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              Last Sold:
                            </span>
                            <span className="font-medium">{comp.lastSold}</span>
                          </div>
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <div className="bg-secondary/30 rounded-lg p-4">
                          <p className="text-sm font-semibold text-foreground mb-1">
                            Sold Price
                          </p>
                          <p className="text-3xl font-bold text-foreground mb-3">
                            {comp.soldPrice}
                          </p>
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">
                              Total Adjustments:
                            </span>
                            <span className={`font-bold ${comp.totalAdjustments.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                              {comp.totalAdjustments}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-base font-bold mt-2 pt-2 border-t">
                            <span className="text-foreground">
                              Final Adjusted Value:
                            </span>
                            <span className="text-primary text-xl">
                              {comp.finalAdjustedValue}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {comp.aiNotes && <div className="mb-4">
                        <h4 className="text-sm font-semibold text-foreground mb-2">
                          AI Analysis Notes:
                        </h4>
                        <p className="text-sm text-muted-foreground leading-relaxed bg-secondary/20 p-4 rounded-lg">
                          {comp.aiNotes}
                        </p>
                      </div>}

                    {comp.aiAdjustments && <div>
                        <h4 className="text-sm font-semibold text-foreground mb-3">
                          Detailed Feature Adjustments:
                        </h4>
                        <div className="grid sm:grid-cols-2 gap-3">
                          {Object.entries(comp.aiAdjustments).map(([key, value]) => <div key={key} className="flex justify-between items-center bg-background p-3 rounded-lg border">
                                <span className="text-sm text-muted-foreground">
                                  {key}:
                                </span>
                                <span className={`text-sm font-semibold ${value === 'No adjustment' ? 'text-muted-foreground' : value.startsWith('-') ? 'text-red-600' : 'text-green-600'}`}>
                                  {value}
                                </span>
                              </div>)}
                        </div>
                      </div>}
                  </CardContent>
                </Card>)}
            </div>
          </div>}

        {/* Assessor Contact Information */}
        <Card className="shadow-lg bg-secondary/20">
          <CardHeader>
            <CardTitle className="text-xl text-primary">
              Assessor Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div>
                <span className="text-sm text-muted-foreground">Office: </span>
                <span className="font-semibold text-foreground">
                  {assessorInfo.name}
                </span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Address: </span>
                <span className="text-foreground">{assessorInfo.address}</span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Phone: </span>
                <span className="text-foreground">{assessorInfo.phone}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>;
};
export default PropertyTaxReport;